/**
 * Onboarding 3.0 platform trial — pure helpers shared by `usePlatformTrial`
 * (the one place every component reads `trialEndsAt` / `requiresApproval` /
 * `readOnly` through) and anything that links to the in-app plans page.
 */

/** The in-app plans page ("Pricing page" in the PRD). The only literal for it. */
export const PLANS_PATH = '/settings/subscription';

/**
 * Every route that renders the in-app plans page (`BillingPage` → `BillingContent`
 * → `NewUserBillingView` answers to both). The only place that should hardcode
 * `'/settings/subscription'` / `'/settings/billing'` as a pair — callers that need
 * to detect "already on the plans page" (e.g. `PlatformTrialController` skipping
 * the auto-open dialog there) use this instead of re-listing the literals.
 */
export const PLANS_PAGE_PATHS = [PLANS_PATH, '/settings/billing'] as const;

const DAY_MS = 86_400_000;

/**
 * Days left in the platform trial, mirroring the backend's own rule
 * (`platformTrialDaysLeft`): `ceil(msLeft / 86_400_000)`, floored at 0 — so the
 * banner and Shopify's `trialDays` quote always agree.
 *
 * Returns null when `trialEndsAt` is absent or unparseable (e.g. an old-flow
 * trial with no platform-trial clock, or a malformed value).
 */
export function trialDaysLeft(
  trialEndsAt: string | undefined | null,
  now: Date
): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt);
  if (Number.isNaN(end.getTime())) return null;
  const msLeft = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(msLeft / DAY_MS));
}
